import { Stack, Typography } from '@mui/material';
import { type CreditPoolWithUnits } from '@vy/api-definition';
import { CREDIT_KINDS, CREDIT_SCHEDULES, type Currency } from '@vy/types';
import { FC, useMemo } from 'react';
import { Price } from '../../../../components/design/Price.tsx';
import { VyLinearProgress } from '../../../../components/new-design/data-display/VyLinearProgress/VyLinearProgress.tsx';
import { formatDateToMDY } from '../../../../utils/date';
import { ContractStatCard } from './components/ContractStatCard/ContractStatCard.tsx';
import { creditPoolEntriesToLedger } from './utils/credit-pool-entries-to-ledger.ts';
import { CreditsUsageDialog } from './CreditsUsageDialog';
import { useDialog } from '../../../../hooks/use-dialog.ts';
import { formatNumber, isNotNullOrUndefined } from '@vy/utils';

type CreditEstimationCardProps = {
  creditPool: CreditPoolWithUnits;
  currency: Currency;
};

export const CreditEstimationCard: FC<CreditEstimationCardProps> = ({ creditPool, currency }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();

  const isMonetary = creditPool.creditKind === CREDIT_KINDS.MONETARY;
  const availableBalance = creditPool.balance;
  const projectedUsedAmount = availableBalance - creditPool.projectedBalance;

  const subItems = useMemo<string[]>(() => {
    const result = [];

    result.push(creditPool.initialGrantSchedule);

    if (isNotNullOrUndefined(creditPool.closestEntryExpiryDate)) {
      const dateLabel =
        creditPool.initialGrantSchedule === CREDIT_SCHEDULES.ONE_TIME ? 'Expires on' : 'Resets';
      result.push(`${dateLabel} ${formatDateToMDY(creditPool.closestEntryExpiryDate)}`);
    }

    return result;
  }, [creditPool]);

  const mainValue = isMonetary ? (
    <Price variant="h5" sx={{ fontWeight: 600 }} currency={currency} fractionDigits={2}>
      {availableBalance}
    </Price>
  ) : (
    <Typography variant="h5" sx={{ fontWeight: 600 }}>
      {`${formatNumber(availableBalance)} ${creditPool.units ?? 'units'}`}
    </Typography>
  );

  return (
    <>
      <ContractStatCard
        cardLabel="Available credit"
        mainValue={mainValue}
        subItems={subItems}
        cta={{
          text: 'See credits ledger',
          onClick: handleOpen,
        }}
      >
        <Stack spacing={3}>
          <VyLinearProgress
            currentValue={projectedUsedAmount}
            totalValue={availableBalance}
            progressColor="#81B3FF"
            labelPlacement="bottom"
            renderLabel={(percentage) => (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                {formatNumber(percentage, 2)}% used
              </Typography>
            )}
          />
        </Stack>
      </ContractStatCard>

      <CreditsUsageDialog
        open={isDialogOpen}
        onClose={handleClose}
        entries={creditPoolEntriesToLedger(creditPool.entries)}
      />
    </>
  );
};
